import { Box, CircularProgress, Link, Typography, alpha } from '@mui/material';
import { ReactNode, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

import { CatalogItemsSelectedCounter } from 'entities/CatalogItem';
import {
    getCatalogItems,
    getCatalogItemsAreLoading,
    getCatalogItemsLoadingMore,
    getCatalogItemsShowDetailedView,
    getCatalogItemsTotalCount,
    getGlobalCatalogTotalCount,
    getSelectedCatalogItem,
    getSelectedCatalogItemsTotal,
} from 'entities/CatalogItem/model/selectors/catalogItemSelectors';
import { getCanAddMarketplaceItemsToOrder } from 'entities/Order/model/selectors/ordersPermissions';
import { AddToPropertyCatalogButton } from 'features/AddCatalogItem';
import { ReviewOrderButton } from 'features/AddItemsToOrder';
import {
    AddItemsBackFromSearchButton,
    SearchCatalogItemsField,
    SearchGlobalCatalogItems,
} from 'features/SearchCatalogItems';
import { useGlobalCatalogInfiniteScroll } from 'features/SearchCatalogItems/lib/hooks/useGlobalCatalogInfiniteScroll';
import { useMarketplaceNavigation } from 'features/SearchCatalogItems/lib/hooks/useMarketplaceNavigation';
import {
    getSearchCatalogItemIsActive,
    getSearchLocalCatalogItemValue,
} from 'features/SearchCatalogItems/model/selectors/searchCatalogItemsSelectors';
import { searchCatalogItemsActions } from 'features/SearchCatalogItems/model/slices/SearchCatalogItemsSlice';
import { itemsContainerHeightOffset } from 'shared/const/styles';
import { useAppDispatch } from 'shared/lib/hooks/useAppDispatch';
import {
    PropertyCatalogItemDataDefiner,
    PropertyCatalogItemsList,
    PropertyCatalogItemsMoveTo,
} from 'widgets/PropertyCatalog';

type SearchCatalogItemsMode = 'catalog' | 'global';

interface SearchCatalogItemsProps {
    catalogId?: string;
    expandable?: boolean;
    moveTo: PropertyCatalogItemsMoveTo;
    isInventory?: boolean;
    showEditButtons?: boolean;
    hideField?: boolean;
    mode?: SearchCatalogItemsMode;
    children?: ReactNode;
}

const fillColumnSx = {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minHeight: 0,
    overflow: 'hidden',
} as const;

interface SearchCatalogSearchFieldProps {
    hideField: boolean;
    isGlobalCatalog: boolean;
    catalogId: string;
    isInventory: boolean;
}

const SearchCatalogSearchField = ({
    hideField,
    isGlobalCatalog,
    catalogId,
    isInventory,
}: SearchCatalogSearchFieldProps) => {
    if (hideField) {
        return null;
    }

    if (isGlobalCatalog) {
        return <SearchGlobalCatalogItems hideBorder />;
    }

    return (
        <SearchCatalogItemsField
            catalogId={catalogId}
            hideBorder
            isInventory={isInventory}
        />
    );
};

interface SearchCatalogBottomActionBarProps {
    isInOrderSection: boolean;
    isMoveToCatalog: boolean;
    isGlobalCatalog: boolean;
    isMoveToOrder: boolean;
    isInventory: boolean;
    totalItemsCount: number;
}

const SearchCatalogBottomActionBar = ({
    isInOrderSection,
    isMoveToCatalog,
    isGlobalCatalog,
    isMoveToOrder,
    isInventory,
    totalItemsCount,
}: SearchCatalogBottomActionBarProps) => {
    return (
        <Box
            data-testid="search-catalog-bottom-action-bar"
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                width: '100%',
                borderTop: '1px solid',
                borderTopColor: 'divider',
                padding: '16px',
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                }}
            >
                {isMoveToOrder && (
                    <AddItemsBackFromSearchButton isInventory={isInventory} />
                )}

                {!isInOrderSection && <CatalogItemsSelectedCounter />}
            </Box>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                }}
            >
                <Box
                    width="250px"
                    display="flex"
                    justifyContent="flex-end"
                    alignItems="center"
                >
                    {isMoveToCatalog && isGlobalCatalog && (
                        <AddToPropertyCatalogButton
                            disabled={totalItemsCount === 0}
                        />
                    )}
                    {isMoveToOrder && <ReviewOrderButton />}
                </Box>
            </Box>
        </Box>
    );
};

export const SearchCatalogItems = ({
    catalogId,
    expandable,
    moveTo,
    isInventory = false,
    showEditButtons = false,
    hideField = false,
    mode = 'catalog',
    children,
}: SearchCatalogItemsProps) => {
    const { t } = useTranslation();
    const location = useLocation();
    const dispatch = useAppDispatch();

    const isActive = useSelector(getSearchCatalogItemIsActive);
    const isLoading = useSelector(getCatalogItemsAreLoading);
    const isLoadingMore = useSelector(getCatalogItemsLoadingMore);
    const catalogItems = useSelector(getCatalogItems);
    const localResultsCount = useSelector(getCatalogItemsTotalCount);
    const globalResultsCount = useSelector(getGlobalCatalogTotalCount);
    const totalItemsCount = useSelector(getSelectedCatalogItemsTotal);
    const selectedItem = useSelector(getSelectedCatalogItem);
    const detailedViewIsShown = useSelector(getCatalogItemsShowDetailedView);
    const localSearchValue = useSelector(getSearchLocalCatalogItemValue);
    const canAddMarketplaceItems = useSelector(
        getCanAddMarketplaceItemsToOrder,
    );

    const isGlobalCatalog = mode === 'global';
    const isOrderEditing = location.state?.selectedOrderId;
    const isMoveToCatalog = moveTo === PropertyCatalogItemsMoveTo.CATALOG;
    const isMoveToOrder = moveTo === PropertyCatalogItemsMoveTo.ORDER;
    const isExpanded = expandable && isActive;
    const resultsCount = isGlobalCatalog
        ? globalResultsCount
        : localResultsCount;
    const { onScroll: onGlobalCatalogScroll } = useGlobalCatalogInfiniteScroll({
        enabled: Boolean(isGlobalCatalog && isExpanded),
    });

    const resultWord = resultsCount === 1 ? t('Result') : t('Results');

    const isInOrderSection = location.pathname.includes('/orders/');

    const actionBarHeight = isMoveToCatalog ? 90 : 144;

    const itemsContainerHeightWithActions = `calc(100vh - ${
        itemsContainerHeightOffset + actionBarHeight
    }px)`;

    const showExpandedView = selectedItem && detailedViewIsShown;

    const { navigateToMarketplace } = useMarketplaceNavigation(
        undefined,
        isInOrderSection,
    );

    const showNoResultsMessage =
        isExpanded &&
        !isLoading &&
        resultsCount === 0 &&
        localSearchValue.length > 0 &&
        !isGlobalCatalog &&
        (isMoveToCatalog || (isMoveToOrder && isInOrderSection));

    const showMarketplaceSearchLink =
        showEditButtons || (isMoveToOrder && canAddMarketplaceItems);
    const showBottomActionBar =
        isExpanded && (!showNoResultsMessage || isMoveToOrder);

    useEffect(
        () => () => {
            dispatch(searchCatalogItemsActions.resetSearch());
        },
        [dispatch],
    );

    const fillsPage = Boolean(children);
    const showSearchDetail = Boolean(isExpanded && showExpandedView);

    return (
        <Box sx={fillsPage ? fillColumnSx : undefined}>
            <Box
                sx={{
                    display: showSearchDetail ? 'none' : 'flex',
                    flexDirection: 'column',
                    flex: fillsPage && isExpanded ? 1 : '0 0 auto',
                    minHeight: 0,
                    overflow: 'hidden',
                    borderRadius: '8px',
                    border: hideField && !isExpanded ? 'none' : '1px solid',
                    borderColor: 'divider',
                }}
            >
                <SearchCatalogSearchField
                    hideField={hideField}
                    isGlobalCatalog={isGlobalCatalog}
                    catalogId={catalogId ?? ''}
                    isInventory={isInventory}
                />
                {isExpanded && (
                    <Box
                        onScroll={onGlobalCatalogScroll}
                        sx={{
                            padding: '0 16px 16px',
                            overflowX: 'hidden',
                            overflowY: 'scroll',
                            flex: 1,
                            minHeight: 0,
                            ...(!fillsPage
                                ? {
                                      maxHeight:
                                          itemsContainerHeightWithActions,
                                  }
                                : {}),
                        }}
                    >
                        {!isLoading && !showNoResultsMessage && (
                            <Box
                                position="sticky"
                                top="0"
                                p="11px 0"
                                borderBottom="1px solid"
                                borderColor="divider"
                                bgcolor="background.paper"
                                zIndex="drawer"
                            >
                                <Typography
                                    variant="subtitle2"
                                    color={(theme) =>
                                        alpha(theme.palette.common.black, 0.5)
                                    }
                                >
                                    {resultsCount} {resultWord} {t('Found')}
                                </Typography>
                            </Box>
                        )}
                        {showNoResultsMessage && (
                            <Box
                                bgcolor="background.paper"
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 0.5,
                                    paddingTop: isInOrderSection ? '16px' : '0',
                                }}
                            >
                                <Typography
                                    data-testid="search-catalog-no-results-message"
                                    variant="subtitle2"
                                    sx={(theme) => ({
                                        lineHeight: '2.4rem',
                                        fontWeight: 400,
                                        color: alpha(
                                            theme.palette.common.black,
                                            0.5,
                                        ),
                                    })}
                                >
                                    {t(
                                        'No items found in the property catalog',
                                    )}
                                    .
                                </Typography>
                                {showMarketplaceSearchLink && (
                                    <Link
                                        data-testid="search-marketplace-link"
                                        component="button"
                                        variant="subtitle2"
                                        sx={{
                                            lineHeight: '2.4rem',
                                            fontWeight: 500,
                                            cursor: 'pointer',
                                        }}
                                        underline="hover"
                                        onClick={() => navigateToMarketplace()}
                                    >
                                        {t('Search the RE-OPS Marketplace')}.
                                    </Link>
                                )}
                            </Box>
                        )}
                        <PropertyCatalogItemsList
                            items={catalogItems ?? []}
                            moveTo={moveTo}
                            loading={isLoading}
                            quickEdit={isOrderEditing}
                            isSearch
                        />
                        {isGlobalCatalog && isLoadingMore && (
                            <Box
                                display="flex"
                                justifyContent="center"
                                alignItems="center"
                                gap="12px"
                                py="20px"
                            >
                                <CircularProgress size={14} />
                                <Typography
                                    variant="subtitle2"
                                    sx={{
                                        color: (theme) =>
                                            alpha(
                                                theme.palette.common.black,
                                                0.5,
                                            ),
                                        fontWeight: 400,
                                    }}
                                >
                                    {t('Loading more items')}
                                </Typography>
                            </Box>
                        )}
                    </Box>
                )}
                {showBottomActionBar && (
                    <SearchCatalogBottomActionBar
                        isInOrderSection={isInOrderSection}
                        isMoveToCatalog={isMoveToCatalog}
                        isGlobalCatalog={isGlobalCatalog}
                        isMoveToOrder={isMoveToOrder}
                        isInventory={isInventory}
                        totalItemsCount={totalItemsCount}
                    />
                )}
            </Box>
            {showSearchDetail && selectedItem && (
                <Box
                    sx={{
                        ...fillColumnSx,
                        padding: '16px',
                        borderRadius: '8px',
                        border: '1px solid',
                        borderColor: 'divider',
                    }}
                >
                    <PropertyCatalogItemDataDefiner
                        isDetailed
                        item={selectedItem}
                        moveTo={moveTo}
                        quickEdit={isOrderEditing}
                        isSearch
                    />
                </Box>
            )}
            {children ? (
                <Box
                    sx={{
                        ...fillColumnSx,
                        marginTop: '20px',
                        borderRadius: '12px 0 0 12px',
                        display: isExpanded ? 'none' : 'flex',
                    }}
                >
                    {children}
                </Box>
            ) : null}
        </Box>
    );
};
